import MarkdownIt from "markdown-it";
import DOMPurify from "dompurify";

// Espelha o renderizador do widget
// (ai-agent-widget/app/src/renderers/markdown.ts) para que o operador leia o
// mesmo texto que o cliente leu. O agente escreve markdown, portanto sem isto
// a transcricao mostra `**329,99€**` e bullets crus, e o operador julga o
// agente por texto que ele nao escreveu.
//
// Duas diferencas deliberadas em relacao ao widget:
//
// 1. O widget carrega markdown-it e dompurify em lazy import para nao os pagar
//    no bundle inicial, que e servido no site do cliente. O backoffice ja e um
//    bundle de aplicacao autenticada, portanto aqui um import estatico e
//    suficiente e dispensa o par ensureMarkdownLoaded/isMarkdownLoaded.
// 2. O widget usa markdown-it-link-attributes, que nao esta instalado aqui.
//    Os mesmos atributos sao postos a mao na regra link_open abaixo.
//
// Mantem-se o typographer e as aspas curvas do widget: a transcricao e prova,
// e o que la esta deve ser o que o cliente viu no ecra.
const md = new MarkdownIt({
  html: false,
  xhtmlOut: true,
  breaks: true,
  langPrefix: "language-",
  linkify: true,
  typographer: true,
  quotes: "“”‘’",
});

const defaultLinkOpen =
  md.renderer.rules.link_open ||
  ((tokens, idx, options, env, self) => self.renderToken(tokens, idx, options));

md.renderer.rules.link_open = (tokens, idx, options, env, self) => {
  tokens[idx].attrSet("target", "_blank");
  tokens[idx].attrSet("rel", "noreferrer noopener nofollow");
  return defaultLinkOpen(tokens, idx, options, env, self);
};

// Lista de atributos igual a do widget menos height e width, que la existem so
// para a regra cw_image_sizes do Chatwoot, que o backoffice nao corre.
const ALLOWED_ATTR = ["href", "src", "alt", "title", "class", "rel", "target"];

// A pre-visualizacao da lista e uma linha de prosa, nao um documento: mostrar
// `**219,99 EUR**` cru numa linha truncada le-se pior do que o texto simples.
// Andar sobre os tokens do proprio markdown-it, em vez de renderizar para HTML
// e despir as etiquetas, evita um DOMParser por linha em cada tick do polling.
export function markdownToPlainText(raw) {
  if (!raw) return "";
  const parts = [];
  const walk = (tokens) => {
    for (const token of tokens) {
      if (token.type === "inline" && token.children) {
        walk(token.children);
        continue;
      }
      if (token.type === "text" || token.type === "code_inline") parts.push(token.content);
      // Um `fence` (```) e um `code_block` (indentado) trazem o texto no proprio
      // token: nao tem filhos por onde descer nem `_close` por onde fechar.
      // Sem este ramo, uma mensagem que so tenha um bloco de codigo achatava
      // para "" e a linha da lista desenhava a segunda linha em branco, porque
      // o preview do backend so salta o conteudo nulo ou em branco e este nao
      // era nem um nem outro.
      else if (token.type === "fence" || token.type === "code_block") parts.push(token.content, " ");
      else if (token.type === "softbreak" || token.type === "hardbreak") parts.push(" ");
      else if (token.block && token.type.endsWith("_close")) parts.push(" ");
    }
  };
  walk(md.parse(raw, {}));
  return parts.join("").replace(/\s+/g, " ").trim();
}

// Devolve HTML ja saneado. Quem consome poe-no com dangerouslySetInnerHTML.
export function renderMarkdownSafe(raw) {
  if (!raw) return "";
  return DOMPurify.sanitize(md.render(raw), { ALLOWED_ATTR });
}
